import React,{useState,useRef,useCallback,useEffect} from 'react';
import {api} from '../lib/api';
import {AGE_CATEGORIES,COUNTRIES} from '../lib/constants';
import {computeLeagueTable,genCode,leagueTeamName,resolveLeagueFixtureScore} from '../lib/utils';

export function LeaguesPage({org,leagues,pendingLeagueInvites,teams,auth,ac,AS,isHead,isMobile,onLeaguesChanged}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [activeLeagueId,setActiveLeagueId]=useState(null);
  const [activeTab,setActiveTab]=useState("table");
  const [showCreate,setShowCreate]=useState(false);
  const [showJoin,setShowJoin]=useState(false);
  const [detail,setDetail]=useState(null); // {league, leagueTeams, leagueFixtures, fixturesById, teamsById}
  const [loadingDetail,setLoadingDetail]=useState(false);

  const activeLeague=leagues.find(l=>l.id===activeLeagueId);
  const isOwner=activeLeague&&org&&activeLeague.owner_org_id===org.id;

  useEffect(()=>{ if(activeLeagueId)loadDetail(activeLeagueId); },[activeLeagueId]);

  async function loadDetail(leagueId){
    setLoadingDetail(true);
    const league=leagues.find(l=>l.id===leagueId);
    const lts=await api.get(auth.token,"league_teams",`league_id=eq.${leagueId}`);
    const lfs=await api.get(auth.token,"league_fixtures",`league_id=eq.${leagueId}&order=match_date.asc`);
    const teamIds=[...new Set((lts||[]).filter(lt=>lt.team_id).map(lt=>lt.team_id))];
    let teamsById={};
    if(teamIds.length>0){
      const ts=await api.get(auth.token,"teams",`id=in.(${teamIds.join(",")})&select=id,name`);
      (ts||[]).forEach(t=>{teamsById[t.id]=t;});
    }
    const fixtureIds=[...new Set((lfs||[]).flatMap(lf=>[lf.home_fixture_id,lf.away_fixture_id]).filter(Boolean))];
    let fixturesById={};
    if(fixtureIds.length>0){
      const fs=await api.get(auth.token,"fixtures",`id=in.(${fixtureIds.join(",")})`);
      (fs||[]).forEach(f=>{fixturesById[f.id]=f;});
    }
    setDetail({league,leagueTeams:lts||[],leagueFixtures:lfs||[],fixturesById,teamsById});
    setLoadingDetail(false);
  }

  async function createLeague(fields,teamNames){
    const created=await api.post(auth.token,"leagues",[{...fields,owner_org_id:org.id,created_by:auth.coach.id}]);
    if(created&&created[0]){
      const leagueId=created[0].id;
      const rows=(teamNames||[]).map(name=>({league_id:leagueId,display_name:name,status:"unclaimed",join_code:genCode()}));
      if(rows.length>0)await api.post(auth.token,"league_teams",rows);
      setShowCreate(false);
      await onLeaguesChanged();
      setActiveLeagueId(leagueId);
    }
  }

  async function respondToInvite(invite,accept){
    await api.patch(auth.token,"league_join_requests",invite.id,{status:accept?"accepted":"declined"});
    if(accept){
      await api.patch(auth.token,"league_teams",invite.league_team_id,{status:"accepted",org_id:org.id});
    }
    await onLeaguesChanged();
  }

  // Leaving a league just marks this org's own league_teams row(s) with
  // left_league_at — it does NOT delete the row, change its team_id, or touch
  // any fixtures/results. That placeholder team stays exactly where it is in
  // the table and everyone else's standings/fixtures are unaffected; this org
  // (and its coaches/parents/players) simply stop seeing the league in their
  // own list and league chat.
  const [leavingLeague,setLeavingLeague]=useState(false);
  const [showLeaveConfirm,setShowLeaveConfirm]=useState(false);
  async function leaveLeague(){
    if(!activeLeague||!org)return;
    setLeavingLeague(true);
    await api.patchWhere(auth.token,"league_teams",`league_id=eq.${activeLeague.id}&org_id=eq.${org.id}`,{left_league_at:new Date().toISOString()});
    setLeavingLeague(false);
    setShowLeaveConfirm(false);
    setActiveLeagueId(null);
    await onLeaguesChanged();
  }

  if(!activeLeagueId){
    return(
      // width:100% with auto side margins, not maxWidth alone. A max width caps
      // how wide the column can grow but says nothing about where the leftover
      // space goes, so on a wide monitor the whole page sat pinned to the left
      // edge of its card with a gulf of empty white beside it. Centring is what
      // the rest of the app does with its content column.
      <div style={{display:"flex",flexDirection:"column",gap:20,width:"100%",maxWidth:760,margin:"0 auto"}}>
        {pendingLeagueInvites.length>0&&(
          <div style={{...C,padding:16,background:"#fef3c7",border:"1px solid #f59e0b55"}}>
            <div style={{fontSize:11,fontWeight:700,color:"#92400e",textTransform:"uppercase",marginBottom:10}}>League Invitations</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {pendingLeagueInvites.map(inv=>(
                <div key={inv.id} style={{background:"#fff",borderRadius:8,padding:"10px 12px",display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                  <span style={{fontSize:12,color:"#92400e"}}>You've been invited to join a league.</span>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>respondToInvite(inv,true)} style={{padding:"6px 12px",borderRadius:6,border:"none",background:"#10b981",color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Accept</button>
                    <button onClick={()=>respondToInvite(inv,false)} style={{padding:"6px 12px",borderRadius:6,border:"1px solid #f59e0b",background:"none",color:"#92400e",fontWeight:700,fontSize:11,cursor:"pointer"}}>Decline</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
          <div style={{fontSize:10,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase"}}>Your Leagues</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={()=>setShowJoin(true)} style={{padding:"8px 14px",borderRadius:8,border:`1px solid ${ac}`,background:"none",color:ac,fontWeight:700,fontSize:12,cursor:"pointer"}}>🔑 Join with Code</button>
            {isHead&&<button onClick={()=>setShowCreate(true)} style={{padding:"8px 14px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>+ Create League</button>}
          </div>
        </div>
        {showJoin&&<JoinLeagueModal org={org} teams={teams} auth={auth} ac={ac} AS={AS} onClose={()=>setShowJoin(false)} onJoined={async()=>{setShowJoin(false);await onLeaguesChanged();}}/>}
        {leagues.length===0?(
          <div style={{...C,padding:36,textAlign:"center",color:"var(--c-muted2)"}}>
            <div style={{fontSize:28,marginBottom:8}}>🏆</div>
            <div style={{fontSize:13,fontWeight:600,color:"var(--c-muted)"}}>No leagues yet</div>
            <div style={{fontSize:11,marginTop:4}}>{isHead?"Create one, or wait for an invitation from another club.":"Ask your head coach to create or join one."}</div>
          </div>
        ):(
          <div style={{display:"flex",flexDirection:"column",gap:8}}>
            {leagues.map(l=>(
              <button key={l.id} onClick={()=>{setActiveLeagueId(l.id);setActiveTab("table");}} style={{...C,padding:"14px 16px",cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <div>
                  <div style={{fontWeight:700,fontSize:14}}>{l.name}</div>
                  <div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>{[l.city_area,l.country].filter(Boolean).join(", ")||"—"} {l.owner_org_id===org.id?"· You own this league":""}</div>
                </div>
                <span style={{fontSize:12,color:"var(--c-muted2)"}}>→</span>
              </button>
            ))}
          </div>
        )}
        {showCreate&&<CreateLeagueModal onClose={()=>setShowCreate(false)} onCreate={createLeague} ac={ac} AS={AS} isMobile={isMobile}/>}
      </div>
    );
  }

  if(loadingDetail||!detail)return <div style={{padding:40,textAlign:"center",color:"var(--c-muted)"}}>Loading league…</div>;

  return(
    <div style={{display:"flex",flexDirection:"column",gap:16,width:"100%",maxWidth:900,margin:"0 auto"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
        <button onClick={()=>setActiveLeagueId(null)} style={{alignSelf:"flex-start",background:"none",border:"none",color:ac,fontSize:11,fontWeight:700,cursor:"pointer",padding:0}}>← All Leagues</button>
        {!isOwner&&isHead&&<button onClick={()=>setShowLeaveConfirm(true)} style={{background:"none",border:"1px solid #ef444455",color:"#ef4444",fontSize:11,fontWeight:700,cursor:"pointer",padding:"6px 12px",borderRadius:7}}>🚪 Leave League</button>}
      </div>
      <div>
        <div style={{fontSize:19,fontWeight:800}}>{detail.league.name}</div>
        <div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>{[detail.league.city_area,detail.league.country].filter(Boolean).join(", ")||"—"}{detail.league.start_date?` · ${detail.league.start_date} to ${detail.league.end_date||"—"}`:""}</div>
      </div>
      {showLeaveConfirm&&(
        <div style={{...C,padding:16,border:"1px solid #ef444455",background:"#7c2d1220"}}>
          <div style={{fontSize:13,fontWeight:700,marginBottom:6}}>Leave {detail.league.name}?</div>
          <div style={{fontSize:11,color:"var(--c-muted)",marginBottom:12,lineHeight:1.5}}>Your team stays in the league table exactly as it is now — fixtures, results, and every other club's standings are unaffected. This just removes the league from your own club's list and league chat; you can rejoin later with a league invite or join code.</div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={leaveLeague} disabled={leavingLeague} style={{padding:"8px 16px",borderRadius:7,border:"none",background:"#ef4444",color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer",opacity:leavingLeague?0.7:1}}>{leavingLeague?"Leaving…":"Yes, Leave League"}</button>
            <button onClick={()=>setShowLeaveConfirm(false)} disabled={leavingLeague} style={{padding:"8px 16px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:12,cursor:"pointer"}}>Cancel</button>
          </div>
        </div>
      )}
      <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3,border:`1px solid ${AS.borderColor}`,maxWidth:420}}>
        {[["table","📋 Table"],["fixtures","📅 Fixtures"],["teams","👥 Teams"],["stats","📊 Stats"]].map(([v,l])=>(
          <button key={v} onClick={()=>setActiveTab(v)} style={{flex:1,padding:"8px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:activeTab===v?ac:"transparent",color:activeTab===v?"#fff":"var(--c-muted)"}}>{l}</button>
        ))}
      </div>
      {activeTab==="table"&&<LeagueTableTab detail={detail} ac={ac} AS={AS}/>}
      {activeTab==="fixtures"&&<LeagueFixturesTab detail={detail} org={org} auth={auth} ac={ac} AS={AS} isOwner={isOwner} isMobile={isMobile} onRefresh={()=>loadDetail(activeLeagueId)}/>}
      {activeTab==="teams"&&<LeagueTeamsTab detail={detail} org={org} teams={teams} auth={auth} ac={ac} AS={AS} isOwner={isOwner} isMobile={isMobile} onRefresh={()=>loadDetail(activeLeagueId)}/>}
      {activeTab==="stats"&&<LeagueStatsTab detail={detail} token={auth.token} ac={ac} AS={AS}/>}
    </div>
  );
}
// ── CreateLeagueModal ──────────────────────────────────────────────────────────

export function CreateLeagueModal({onClose,onCreate,ac,AS,isMobile}){
  const [step,setStep]=useState(1);
  const [name,setName]=useState("");
  const [country,setCountry]=useState("");
  const [cityArea,setCityArea]=useState("");
  const [gender,setGender]=useState("");
  const [ageCategory,setAgeCategory]=useState("");
  const [startDate,setStartDate]=useState("");
  const [endDate,setEndDate]=useState("");
  const [pointsWin,setPointsWin]=useState(3);
  const [pointsDraw,setPointsDraw]=useState(1);
  const [pointsLoss,setPointsLoss]=useState(0);
  const [tiebreak,setTiebreak]=useState(["GD","GF","GA","H2H"]);
  const [numTeams,setNumTeams]=useState(8);
  const [teamNames,setTeamNames]=useState([]);
  const [saving,setSaving]=useState(false);
  const TB_LABELS={GD:"Goal Difference",GF:"Goals For",GA:"Goals Against",H2H:"Head-to-Head"};
  function moveTb(i,dir){
    const next=[...tiebreak];
    const j=i+dir;
    if(j<0||j>=next.length)return;
    [next[i],next[j]]=[next[j],next[i]];
    setTiebreak(next);
  }
  function goToStep2(){
    if(!name.trim()){alert("Please enter a league name");return;}
    if(!gender){alert("Please choose which gender this league is for");return;}
    if(!ageCategory){alert("Please choose an age category");return;}
    if(!numTeams||numTeams<2){alert("A league needs at least 2 teams");return;}
    setTeamNames(prev=>{
      const next=Array.from({length:numTeams},(_,i)=>prev[i]||"");
      return next;
    });
    setStep(2);
  }
  async function submit(){
    if(teamNames.some(n=>!n.trim())){alert("Please name every team slot — you can rename them later once clubs join.");return;}
    setSaving(true);
    await onCreate({name:name.trim(),country:country||null,city_area:cityArea.trim()||null,gender,age_category:ageCategory,start_date:startDate||null,end_date:endDate||null,points_win:pointsWin,points_draw:pointsDraw,points_loss:pointsLoss,tiebreak_order:tiebreak},teamNames.map(n=>n.trim()));
    setSaving(false);
  }
  const lbl={display:"block",fontSize:10,fontWeight:600,color:"var(--c-muted)",marginBottom:4};
  const inp={width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,boxSizing:"border-box"};
  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:14,padding:22,width:"100%",maxWidth:460,maxHeight:"88vh",overflowY:"auto"}}>
        <div style={{fontSize:15,fontWeight:800,marginBottom:16}}>{step===1?"Create League":"Name Your Teams"}</div>
        {step===1?(<>
        <div style={{display:"flex",flexDirection:"column",gap:12}}>
          <div><label style={lbl}>League Name</label><input value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. Sunday U19 League" style={inp}/></div>
          <div style={{display:"flex",gap:10}}>
            <div style={{flex:1}}>
              <label style={lbl}>Gender</label>
              <select value={gender} onChange={e=>setGender(e.target.value)} style={inp}>
                <option value="">Choose…</option>
                <option value="male">♂ Boys / Male</option>
                <option value="female">♀ Girls / Female</option>
              </select>
            </div>
            <div style={{flex:1}}>
              <label style={lbl}>Age Category</label>
              <select value={ageCategory} onChange={e=>setAgeCategory(e.target.value)} style={inp}>
                <option value="">Choose…</option>
                {AGE_CATEGORIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <div style={{flex:1}}>
              <label style={lbl}>Country</label>
              <select value={country} onChange={e=>setCountry(e.target.value)} style={inp}>
                <option value="">Choose…</option>
                {COUNTRIES.map(c=><option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{flex:1}}><label style={lbl}>City / Area</label><input value={cityArea} onChange={e=>setCityArea(e.target.value)} style={inp}/></div>
          </div>
          <div style={{display:"flex",gap:10}}>
            <div style={{flex:1}}><label style={lbl}>Start Date</label><input type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} style={inp}/></div>
            <div style={{flex:1}}><label style={lbl}>End Date</label><input type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} style={inp}/></div>
          </div>
          <div>
            <label style={lbl}>Points: Win / Draw / Loss</label>
            <div style={{display:"flex",gap:10}}>
              <input type="number" value={pointsWin} onChange={e=>setPointsWin(+e.target.value)} style={inp}/>
              <input type="number" value={pointsDraw} onChange={e=>setPointsDraw(+e.target.value)} style={inp}/>
              <input type="number" value={pointsLoss} onChange={e=>setPointsLoss(+e.target.value)} style={inp}/>
            </div>
          </div>
          <div>
            <label style={lbl}>Tiebreaker Priority (top = checked first)</label>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {tiebreak.map((tb,i)=>(
                <div key={tb} style={{display:"flex",alignItems:"center",gap:8,background:AS.bgColor,borderRadius:7,padding:"7px 10px"}}>
                  <span style={{fontSize:11,fontWeight:700,color:ac,width:16}}>{i+1}</span>
                  <span style={{flex:1,fontSize:12,fontWeight:600}}>{TB_LABELS[tb]}</span>
                  <button onClick={()=>moveTb(i,-1)} disabled={i===0} style={{background:"none",border:"none",cursor:i===0?"default":"pointer",color:i===0?"var(--c-muted2)":"var(--c-muted)",fontSize:12}}>▲</button>
                  <button onClick={()=>moveTb(i,1)} disabled={i===tiebreak.length-1} style={{background:"none",border:"none",cursor:i===tiebreak.length-1?"default":"pointer",color:i===tiebreak.length-1?"var(--c-muted2)":"var(--c-muted)",fontSize:12}}>▼</button>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label style={lbl}>Number of Teams</label>
            <input type="number" min={2} max={40} value={numTeams} onChange={e=>setNumTeams(+e.target.value)} style={inp}/>
            <div style={{fontSize:10,color:"var(--c-muted2)",marginTop:4}}>You'll name each team next — every team gets its own join code to share with that team's coach.</div>
          </div>
        </div>
        <div style={{display:"flex",gap:10,marginTop:20}}>
          <button onClick={goToStep2} style={{flex:1,padding:"11px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer"}}>Next: Name Teams →</button>
          <button onClick={onClose} style={{padding:"11px 18px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:13,cursor:"pointer"}}>Cancel</button>
        </div>
        </>):(<>
        <div style={{fontSize:11,color:"var(--c-muted2)",marginBottom:14,lineHeight:1.6}}>Name every team in the league. These are placeholder names — once a club joins using their code, they can choose the name that shows publicly on the table.</div>
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
          {teamNames.map((tn,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:11,fontWeight:700,color:ac,width:20}}>{i+1}</span>
              <input value={tn} onChange={e=>setTeamNames(prev=>prev.map((v,j)=>j===i?e.target.value:v))} placeholder={`Team ${i+1} name`} style={inp}/>
            </div>
          ))}
        </div>
        <div style={{display:"flex",gap:10}}>
          <button onClick={submit} disabled={saving} style={{flex:1,padding:"11px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer",opacity:saving?0.7:1}}>{saving?"Creating…":"Create League"}</button>
          <button onClick={()=>setStep(1)} disabled={saving} style={{padding:"11px 18px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:13,cursor:"pointer"}}>← Back</button>
        </div>
        </>)}
      </div>
    </div>
  );
}

// ── LeagueTableTab ──────────────────────────────────────────────────────────────
// ── JoinLeagueModal ─────────────────────────────────────────────────────────────

export function JoinLeagueModal({org,teams,auth,ac,AS,onClose,onJoined}){
  const [code,setCode]=useState("");
  const [checking,setChecking]=useState(false);
  const [err,setErr]=useState("");
  const [found,setFound]=useState(null); // {leagueTeam, league}
  const [selectedTeamId,setSelectedTeamId]=useState("");
  const [displayName,setDisplayName]=useState("");
  const [saving,setSaving]=useState(false);
  const lbl={display:"block",fontSize:10,fontWeight:600,color:"var(--c-muted)",marginBottom:4};
  const inp={width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,boxSizing:"border-box"};

  async function lookupCode(){
    if(!code.trim()){setErr("Please enter a join code");return;}
    setChecking(true);setErr("");
    const rows=await api.get(auth.token,"league_teams",`join_code=eq.${code.trim().toUpperCase()}`);
    if(!rows||rows.length===0){setErr("Invalid join code — check with the league organiser");setChecking(false);return;}
    const lt=rows[0];
    if(lt.status!=="unclaimed"){setErr("This team slot has already been claimed.");setChecking(false);return;}
    const leagues_=await api.get(auth.token,"leagues",`id=eq.${lt.league_id}`);
    if(!leagues_||leagues_.length===0){setErr("Couldn't find that league — please try again.");setChecking(false);return;}
    setFound({leagueTeam:lt,league:leagues_[0]});
    setDisplayName(lt.display_name||"");
    setChecking(false);
  }
  async function confirmJoin(){
    if(!selectedTeamId){alert("Please choose which of your teams is entering this league");return;}
    setSaving(true);
    const team=teams.find(t=>t.id===selectedTeamId);
    await api.patch(auth.token,"league_teams",found.leagueTeam.id,{team_id:selectedTeamId,org_id:org.id,status:"accepted",display_name:displayName.trim()||team?.name||found.leagueTeam.display_name});
    setSaving(false);
    onJoined();
  }

  const qualifyingTeams=(teams||[]).filter(t=>(!found?.league.gender||t.gender===found.league.gender)&&(!found?.league.age_category||t.age_category===found.league.age_category));

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:300,padding:16}} onClick={onClose}>
      <div onClick={e=>e.stopPropagation()} style={{background:AS.surfaceColor,borderRadius:14,padding:22,width:"100%",maxWidth:420,maxHeight:"88vh",overflowY:"auto"}}>
        <div style={{fontSize:15,fontWeight:800,marginBottom:16}}>Join a League</div>
        {!found?(
          <div>
            <label style={lbl}>Join Code</label>
            <input value={code} onChange={e=>setCode(e.target.value.toUpperCase())} onKeyDown={e=>e.key==="Enter"&&lookupCode()} placeholder="ABC123" maxLength={8} style={{...inp,textTransform:"uppercase",letterSpacing:"0.12em",fontWeight:700,fontSize:16,textAlign:"center",marginBottom:10}}/>
            {err&&<div style={{fontSize:11,color:"#ef4444",marginBottom:10}}>{err}</div>}
            <div style={{display:"flex",gap:10}}>
              <button onClick={lookupCode} disabled={checking} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer"}}>{checking?"Checking…":"Find League"}</button>
              <button onClick={onClose} style={{padding:"10px 16px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:13,cursor:"pointer"}}>Cancel</button>
            </div>
          </div>
        ):(
          <div>
            <div style={{background:AS.bgColor,borderRadius:9,padding:12,marginBottom:14}}>
              <div style={{fontWeight:700,fontSize:13}}>{found.league.name}</div>
              <div style={{fontSize:11,color:"var(--c-muted)",marginTop:2}}>{found.league.gender==="male"?"♂ Boys":"♀ Girls"} · {found.league.age_category} · Joining as "{found.leagueTeam.display_name}"</div>
            </div>
            <label style={lbl}>Which of your teams is entering?</label>
            {qualifyingTeams.length===0?(
              <div style={{fontSize:12,color:"#ef4444",marginBottom:12}}>None of your teams match this league's gender/age category ({found.league.gender==="male"?"Boys":"Girls"} {found.league.age_category}).</div>
            ):(
              <select value={selectedTeamId} onChange={e=>{setSelectedTeamId(e.target.value);const t=teams.find(x=>x.id===e.target.value);if(t&&!displayName)setDisplayName(t.name);}} style={{...inp,marginBottom:12}}>
                <option value="">Choose a team…</option>
                {qualifyingTeams.map(t=><option key={t.id} value={t.id}>{t.badge} {t.name}</option>)}
              </select>
            )}
            <label style={lbl}>Public Display Name (shown on the league table)</label>
            <input value={displayName} onChange={e=>setDisplayName(e.target.value)} placeholder={found.leagueTeam.display_name} style={{...inp,marginBottom:16}}/>
            <div style={{display:"flex",gap:10}}>
              <button onClick={confirmJoin} disabled={saving||qualifyingTeams.length===0} style={{flex:1,padding:"10px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:13,cursor:"pointer",opacity:saving?0.7:1}}>{saving?"Joining…":"Confirm & Join"}</button>
              <button onClick={()=>setFound(null)} style={{padding:"10px 16px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:13,cursor:"pointer"}}>← Back</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function LeagueTableTab({detail,ac,AS,teamId}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const table=computeLeagueTable(detail.league,detail.leagueTeams,detail.leagueFixtures,detail.fixturesById,detail.teamsById);
  const myLeagueTeamId=teamId?detail.leagueTeams.find(lt=>lt.team_id===teamId)?.id:null;
  return(
    <div style={{...C,overflow:"hidden"}}>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:520}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${AS.borderColor}`,textAlign:"center"}}>
              <th style={{padding:"9px 10px",textAlign:"left",fontSize:9,color:"var(--c-text)",textTransform:"uppercase"}}>#</th>
              <th style={{padding:"9px 10px",textAlign:"left",fontSize:9,color:"var(--c-text)",textTransform:"uppercase"}}>Team</th>
              {["MP","W","D","L","GF","GA","GD","PTS"].map(h=><th key={h} style={{padding:"9px 8px",fontSize:9,color:"var(--c-text)",textTransform:"uppercase"}}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {table.map((row,i)=>{
              const isMyTeam=myLeagueTeamId&&row.id===myLeagueTeamId;
              return(
              <tr key={row.id} style={{borderBottom:`1px solid ${AS.borderColor}`,background:isMyTeam?ac+"14":"transparent"}}>
                <td style={{padding:"9px 10px",fontWeight:700,color:"var(--c-muted)"}}>{i+1}</td>
                <td style={{padding:"9px 10px",fontWeight:700}}>{isMyTeam&&"⭐ "}{row.name}</td>
                <td style={{padding:"9px 8px",textAlign:"center"}}>{row.played}</td>
                <td style={{padding:"9px 8px",textAlign:"center"}}>{row.won}</td>
                <td style={{padding:"9px 8px",textAlign:"center"}}>{row.drawn}</td>
                <td style={{padding:"9px 8px",textAlign:"center"}}>{row.lost}</td>
                <td style={{padding:"9px 8px",textAlign:"center"}}>{row.gf}</td>
                <td style={{padding:"9px 8px",textAlign:"center"}}>{row.ga}</td>
                <td style={{padding:"9px 8px",textAlign:"center"}}>{row.gd>0?`+${row.gd}`:row.gd}</td>
                <td style={{padding:"9px 8px",textAlign:"center",fontWeight:800,color:ac}}>{row.pts}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {table.length===0&&<div style={{padding:28,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No teams yet.</div>}
    </div>
  );
}

// ── LeagueStatsTab ──────────────────────────────────────────────────────────────

export function LeagueStatsTab({detail,token,ac,AS}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [events,setEvents]=useState(null);
  const [playersById,setPlayersById]=useState({});
  useEffect(()=>{
    const fixtureIds=Object.keys(detail.fixturesById);
    if(fixtureIds.length===0){setEvents([]);return;}
    api.get(token,"fixture_events",`fixture_id=in.(${fixtureIds.join(",")})`).then(async rows=>{
      setEvents(rows||[]);
      const pids=[...new Set((rows||[]).map(e=>e.player_id))];
      if(pids.length>0){
        const ps=await api.get(token,"players",`id=in.(${pids.join(",")})&select=id,name`);
        const map={};(ps||[]).forEach(p=>{map[p.id]=p.name;});
        setPlayersById(map);
      }
    });
  },[detail]);
  if(events===null)return <div style={{padding:30,textAlign:"center",color:"var(--c-muted)"}}>Loading stats…</div>;

  const goalsByPlayer={},assistsByPlayer={};
  events.forEach(e=>{
    if(e.type==="goal")goalsByPlayer[e.player_id]=(goalsByPlayer[e.player_id]||0)+(e.count||1);
    else assistsByPlayer[e.player_id]=(assistsByPlayer[e.player_id]||0)+(e.count||1);
  });
  const topScorers=Object.entries(goalsByPlayer).sort((a,b)=>b[1]-a[1]).slice(0,5);
  const topAssisters=Object.entries(assistsByPlayer).sort((a,b)=>b[1]-a[1]).slice(0,5);

  // Clean sheets + goals conceded, per league_team, from resolved confirmed fixtures
  const teamStats={};
  detail.leagueTeams.forEach(lt=>{teamStats[lt.id]={id:lt.id,name:leagueTeamName(lt,detail.teamsById),cleanSheets:0,conceded:0,played:0};});
  detail.leagueFixtures.forEach(lf=>{
    const r=resolveLeagueFixtureScore(lf,detail.fixturesById);
    if(r.status!=="confirmed")return;
    const home=teamStats[lf.home_league_team_id],away=teamStats[lf.away_league_team_id];
    if(home){home.played++;home.conceded+=r.away;if(r.away===0)home.cleanSheets++;}
    if(away){away.played++;away.conceded+=r.home;if(r.home===0)away.cleanSheets++;}
  });
  const mostCleanSheets=Object.values(teamStats).filter(t=>t.played>0).sort((a,b)=>b.cleanSheets-a.cleanSheets).slice(0,5);
  const leastConceded=Object.values(teamStats).filter(t=>t.played>0).sort((a,b)=>a.conceded-b.conceded).slice(0,5);

  function StatCard({title,rows,renderRow}){
    return(
      <div style={{...C,padding:16}}>
        <div style={{fontSize:11,fontWeight:700,color:"var(--c-text)",textTransform:"uppercase",marginBottom:12}}>{title}</div>
        {rows.length===0?<div style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>No data yet.</div>:
          <div style={{display:"flex",flexDirection:"column",gap:8}}>{rows.map((r,i)=>renderRow(r,i))}</div>}
      </div>
    );
  }

  return(
    <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))",gap:16}}>
      <StatCard title="🥅 Top Scorers" rows={topScorers} renderRow={([playerId,count],i)=>(
        <div key={playerId} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12}}><b style={{color:ac}}>{i+1}.</b> {playersById[playerId]||"Unknown Player"}</span>
          <span style={{fontWeight:800,fontSize:13}}>{count}</span>
        </div>
      )}/>
      <StatCard title="🎯 Top Assisters" rows={topAssisters} renderRow={([playerId,count],i)=>(
        <div key={playerId} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12}}><b style={{color:ac}}>{i+1}.</b> {playersById[playerId]||"Unknown Player"}</span>
          <span style={{fontWeight:800,fontSize:13}}>{count}</span>
        </div>
      )}/>
      <StatCard title="🧤 Most Clean Sheets" rows={mostCleanSheets} renderRow={(t,i)=>(
        <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12}}><b style={{color:ac}}>{i+1}.</b> {t.name}</span>
          <span style={{fontWeight:800,fontSize:13}}>{t.cleanSheets}</span>
        </div>
      )}/>
      <StatCard title="🛡️ Fewest Goals Conceded" rows={leastConceded} renderRow={(t,i)=>(
        <div key={t.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span style={{fontSize:12}}><b style={{color:ac}}>{i+1}.</b> {t.name}</span>
          <span style={{fontWeight:800,fontSize:13}}>{t.conceded}</span>
        </div>
      )}/>
    </div>
  );
}
// ── LeagueTeamsTab ──────────────────────────────────────────────────────────────

export function LeagueTeamsTab({detail,org,teams,auth,ac,AS,isOwner,isMobile,onRefresh}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [showAdd,setShowAdd]=useState(false);
  const [mode,setMode]=useState("own"); // "own" | "new"
  const [newSlotName,setNewSlotName]=useState("");
  const [saving,setSaving]=useState(false);
  const [copiedId,setCopiedId]=useState(null);

  const myUnaddedTeams=(teams||[]).filter(t=>!detail.leagueTeams.some(lt=>lt.team_id===t.id)&&(!detail.league.gender||t.gender===detail.league.gender)&&(!detail.league.age_category||t.age_category===detail.league.age_category));

  async function addOwnTeam(team){
    setSaving(true);
    await api.post(auth.token,"league_teams",[{league_id:detail.league.id,team_id:team.id,org_id:org.id,display_name:team.name,status:"accepted"}]);
    resetAddForm();
    onRefresh();
    setSaving(false);
  }
  async function addNewSlot(){
    if(!newSlotName.trim())return;
    setSaving(true);
    await api.post(auth.token,"league_teams",[{league_id:detail.league.id,display_name:newSlotName.trim(),status:"unclaimed",join_code:genCode()}]);
    resetAddForm();
    onRefresh();
    setSaving(false);
  }
  function resetAddForm(){
    setShowAdd(false);setMode("own");setNewSlotName("");
  }
  async function removeTeam(lt){
    if(!window.confirm(`Remove ${leagueTeamName(lt,detail.teamsById)} from this league? Their fixtures in this league will remain but won't count for a team.`))return;
    await api.del(auth.token,"league_teams",lt.id);
    onRefresh();
  }
  function copyCode(lt){
    navigator.clipboard.writeText(lt.join_code);
    setCopiedId(lt.id);
    setTimeout(()=>setCopiedId(id=>id===lt.id?null:id),1800);
  }

  const statusLabel={accepted:["✓ Joined","#10b981"],pending:["⏳ Invited","#f59e0b"],external:["🌐 External","var(--c-muted)"],unclaimed:["🔑 Unclaimed","#f59e0b"]};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {isOwner&&(showAdd?(
        <div style={{...C,padding:18}}>
          <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Add a Team</div>
          <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3,marginBottom:14,maxWidth:320}}>
            {[["own","🏠 My Own Teams"],["new","➕ New Team Slot"]].map(([v,l])=>(
              <button key={v} onClick={()=>setMode(v)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:mode===v?ac:"transparent",color:mode===v?"#fff":"var(--c-muted)"}}>{l}</button>
            ))}
          </div>
          {mode==="own"?(
            myUnaddedTeams.length===0?(
              <div style={{fontSize:11,color:"var(--c-muted2)"}}>No eligible teams to add — either all your qualifying teams are already in this league, or none of your teams match this league's gender/age category.</div>
            ):(
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                {myUnaddedTeams.map(t=>(
                  <button key={t.id} onClick={()=>addOwnTeam(t)} disabled={saving} style={{textAlign:"left",padding:"9px 12px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,cursor:"pointer",fontSize:12,fontWeight:600}}>{t.badge} {t.name}</button>
                ))}
              </div>
            )
          ):(
            <div>
              <label style={{fontSize:10,fontWeight:600,color:"var(--c-muted)",display:"block",marginBottom:4}}>Team Name</label>
              <input value={newSlotName} onChange={e=>setNewSlotName(e.target.value)} placeholder="e.g. Riverside FC" style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,boxSizing:"border-box",marginBottom:12}}/>
              <div style={{fontSize:10,color:"var(--c-muted2)",marginBottom:12}}>This creates a placeholder slot with its own join code. Share the code with that team's coach — until then, you can enter their match results manually.</div>
              <button onClick={addNewSlot} disabled={saving} style={{padding:"9px 16px",borderRadius:7,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{saving?"Adding…":"Add Slot"}</button>
            </div>
          )}
          <button onClick={resetAddForm} style={{marginTop:14,background:"none",border:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer",padding:0}}>Cancel</button>
        </div>
      ):(
        <button onClick={()=>setShowAdd(true)} style={{alignSelf:"flex-start",padding:"9px 16px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>+ Add Team</button>
      ))}
      <div style={{...C,overflow:"hidden"}}>
        {detail.leagueTeams.length===0?(
          <div style={{padding:28,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No teams added yet.</div>
        ):detail.leagueTeams.map(lt=>{
          const [label,color]=statusLabel[lt.status]||["—","var(--c-muted)"];
          return(
            <div key={lt.id} style={{padding:"11px 16px",borderBottom:`1px solid ${AS.borderColor}`,display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
              <span style={{fontSize:13,fontWeight:600}}>{leagueTeamName(lt,detail.teamsById)}</span>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:10,fontWeight:700,color}}>{label}</span>
                {isOwner&&lt.status==="unclaimed"&&lt.join_code&&(
                  <button onClick={()=>copyCode(lt)} title="Copy join code" style={{display:"flex",alignItems:"center",gap:5,padding:"4px 9px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,cursor:"pointer",fontSize:11,fontWeight:700,letterSpacing:"0.06em",color:copiedId===lt.id?"#10b981":"var(--c-text2)"}}>
                    {copiedId===lt.id?"✓ Copied":lt.join_code}{copiedId!==lt.id&&" 📋"}
                  </button>
                )}
                {isOwner&&<button onClick={()=>removeTeam(lt)} style={{background:"none",border:"none",color:"#ef4444",fontSize:11,cursor:"pointer",padding:0}}>Remove</button>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ── LeagueFixturesTab ────────────────────────────────────────────────────────────

export function LeagueFixturesTab({detail,org,auth,ac,AS,isOwner,isMobile,onRefresh}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [showAdd,setShowAdd]=useState(false);
  const [showCsv,setShowCsv]=useState(false);
  const [homeLtId,setHomeLtId]=useState("");
  const [awayLtId,setAwayLtId]=useState("");
  const [matchDate,setMatchDate]=useState("");
  const [kickoff,setKickoff]=useState("");
  const [venue,setVenue]=useState("");
  const [saving,setSaving]=useState(false);
  const [csvText,setCsvText]=useState("");
  const [csvBusy,setCsvBusy]=useState(false);
  const [csvResult,setCsvResult]=useState(null);
  const [overrideId,setOverrideId]=useState(null);
  const [overrideHome,setOverrideHome]=useState("");
  const [overrideAway,setOverrideAway]=useState("");

  useEffect(()=>{ syncMyLinks(); },[detail.leagueFixtures.length]);

  async function syncMyLinks(){
    let changed=false;
    for(const lf of detail.leagueFixtures){
      const homeLt=detail.leagueTeams.find(lt=>lt.id===lf.home_league_team_id);
      const awayLt=detail.leagueTeams.find(lt=>lt.id===lf.away_league_team_id);
      if(homeLt&&homeLt.org_id===org.id&&homeLt.team_id&&!lf.home_fixture_id){
        const created=await api.post(auth.token,"fixtures",[{team_id:homeLt.team_id,opponent:leagueTeamName(awayLt,detail.teamsById),date:lf.match_date,kickoff:lf.kickoff_time||null,venue:lf.venue||null,played:false}]);
        if(created&&created[0]){await api.patch(auth.token,"league_fixtures",lf.id,{home_fixture_id:created[0].id});changed=true;}
      }
      if(awayLt&&awayLt.org_id===org.id&&awayLt.team_id&&!lf.away_fixture_id){
        const created=await api.post(auth.token,"fixtures",[{team_id:awayLt.team_id,opponent:leagueTeamName(homeLt,detail.teamsById),date:lf.match_date,kickoff:lf.kickoff_time||null,venue:lf.venue||null,played:false}]);
        if(created&&created[0]){await api.patch(auth.token,"league_fixtures",lf.id,{away_fixture_id:created[0].id});changed=true;}
      }
    }
    if(changed)onRefresh();
  }

  async function addFixture(){
    if(!homeLtId||!awayLtId||homeLtId===awayLtId){alert("Please choose two different teams");return;}
    if(!matchDate){alert("Please choose a match date");return;}
    setSaving(true);
    await api.post(auth.token,"league_fixtures",[{league_id:detail.league.id,home_league_team_id:homeLtId,away_league_team_id:awayLtId,match_date:matchDate,kickoff_time:kickoff||null,venue:venue||null}]);
    setSaving(false);setShowAdd(false);setHomeLtId("");setAwayLtId("");setMatchDate("");setKickoff("");setVenue("");
    onRefresh();
  }

  async function importCsv(){
    // Expected columns: home_team,away_team,date,kickoff,venue — home_team/away_team
    // matched by exact name against teams already added to this league.
    setCsvBusy(true);
    const lines=csvText.trim().split("\n").filter(Boolean);
    const header=lines[0].toLowerCase().split(",").map(h=>h.trim());
    const idx={home:header.indexOf("home_team"),away:header.indexOf("away_team"),date:header.indexOf("date"),kickoff:header.indexOf("kickoff"),venue:header.indexOf("venue")};
    if(idx.home===-1||idx.away===-1||idx.date===-1){
      setCsvResult({ok:false,message:"CSV must have at least home_team, away_team, and date columns."});
      setCsvBusy(false);return;
    }
    const nameToLt={};
    detail.leagueTeams.forEach(lt=>{nameToLt[leagueTeamName(lt,detail.teamsById).trim().toLowerCase()]=lt;});
    let created=0,skipped=[];
    for(let i=1;i<lines.length;i++){
      const cols=lines[i].split(",").map(c=>c.trim());
      const homeLt=nameToLt[(cols[idx.home]||"").toLowerCase()];
      const awayLt=nameToLt[(cols[idx.away]||"").toLowerCase()];
      if(!homeLt||!awayLt){skipped.push(cols[idx.home]+" vs "+cols[idx.away]);continue;}
      await api.post(auth.token,"league_fixtures",[{league_id:detail.league.id,home_league_team_id:homeLt.id,away_league_team_id:awayLt.id,match_date:cols[idx.date]||null,kickoff_time:idx.kickoff>-1?cols[idx.kickoff]||null:null,venue:idx.venue>-1?cols[idx.venue]||null:null}]);
      created++;
    }
    setCsvResult({ok:true,message:`Imported ${created} fixture${created===1?"":"s"}.`+(skipped.length>0?` Skipped ${skipped.length} row(s) with unrecognised team names: ${skipped.join("; ")}`:"")});
    setCsvBusy(false);
    onRefresh();
  }

  async function saveOverride(lf){
    const h=parseInt(overrideHome,10),a=parseInt(overrideAway,10);
    if(isNaN(h)||isNaN(a)){alert("Please enter both scores as numbers");return;}
    await api.patch(auth.token,"league_fixtures",lf.id,{owner_final_home_score:h,owner_final_away_score:a});
    setOverrideId(null);setOverrideHome("");setOverrideAway("");
    onRefresh();
  }

  const inp={padding:"9px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:13,boxSizing:"border-box"};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {isOwner&&(
        <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
          <button onClick={()=>{setShowAdd(v=>!v);setShowCsv(false);}} style={{padding:"9px 16px",borderRadius:8,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{showAdd?"Cancel":"+ Add Fixture"}</button>
          <button onClick={()=>{setShowCsv(v=>!v);setShowAdd(false);}} style={{padding:"9px 16px",borderRadius:8,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-text2)",fontWeight:700,fontSize:12,cursor:"pointer"}}>{showCsv?"Cancel":"📥 Import CSV"}</button>
        </div>
      )}
      {showAdd&&(
        <div style={{...C,padding:18}}>
          <div style={{display:"flex",gap:10,marginBottom:10,flexWrap:"wrap"}}>
            <select value={homeLtId} onChange={e=>setHomeLtId(e.target.value)} style={{...inp,flex:1,minWidth:140}}>
              <option value="">Home team…</option>
              {detail.leagueTeams.map(lt=><option key={lt.id} value={lt.id}>{leagueTeamName(lt,detail.teamsById)}</option>)}
            </select>
            <select value={awayLtId} onChange={e=>setAwayLtId(e.target.value)} style={{...inp,flex:1,minWidth:140}}>
              <option value="">Away team…</option>
              {detail.leagueTeams.map(lt=><option key={lt.id} value={lt.id}>{leagueTeamName(lt,detail.teamsById)}</option>)}
            </select>
          </div>
          <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}}>
            <input type="date" value={matchDate} onChange={e=>setMatchDate(e.target.value)} style={{...inp,flex:1,minWidth:130}}/>
            <input value={kickoff} onChange={e=>setKickoff(e.target.value)} placeholder="Kickoff (e.g. 14:00)" style={{...inp,flex:1,minWidth:130}}/>
            <input value={venue} onChange={e=>setVenue(e.target.value)} placeholder="Venue" style={{...inp,flex:1,minWidth:130}}/>
          </div>
          <button onClick={addFixture} disabled={saving} style={{padding:"9px 16px",borderRadius:7,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{saving?"Adding…":"Add Fixture"}</button>
        </div>
      )}
      {showCsv&&(
        <div style={{...C,padding:18}}>
          <div style={{fontSize:12,color:"var(--c-muted2)",marginBottom:10,lineHeight:1.6}}>Paste CSV with columns <code>home_team,away_team,date,kickoff,venue</code> (kickoff/venue optional). Team names must exactly match the names already added on the Teams tab.</div>
          <textarea value={csvText} onChange={e=>setCsvText(e.target.value)} rows={6} placeholder={"home_team,away_team,date,kickoff,venue\nRiverside FC,U19 Girls,2026-09-12,14:00,Home Ground"} style={{width:"100%",padding:"9px 11px",borderRadius:7,border:`1px solid ${AS.borderColor}`,background:AS.bgColor,color:"var(--c-text)",fontSize:12,fontFamily:"monospace",boxSizing:"border-box",marginBottom:10,resize:"vertical"}}/>
          <button onClick={importCsv} disabled={csvBusy||!csvText.trim()} style={{padding:"9px 16px",borderRadius:7,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer"}}>{csvBusy?"Importing…":"Import"}</button>
          {csvResult&&<div style={{marginTop:10,fontSize:11,color:csvResult.ok?"#10b981":"#ef4444",lineHeight:1.6}}>{csvResult.message}</div>}
        </div>
      )}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {detail.leagueFixtures.length===0?(
          <div style={{...C,padding:28,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No fixtures yet.</div>
        ):detail.leagueFixtures.map(lf=>{
          const homeLt=detail.leagueTeams.find(lt=>lt.id===lf.home_league_team_id);
          const awayLt=detail.leagueTeams.find(lt=>lt.id===lf.away_league_team_id);
          const resolved=resolveLeagueFixtureScore(lf,detail.fixturesById);
          return(
            <div key={lf.id} style={{...C,padding:"12px 16px",borderColor:resolved.status==="disputed"?"#ef4444":AS.borderColor,borderWidth:resolved.status==="disputed"?2:1}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:8}}>
                <div>
                  <div style={{fontWeight:700,fontSize:13}}>{leagueTeamName(homeLt,detail.teamsById)} vs {leagueTeamName(awayLt,detail.teamsById)}</div>
                  <div style={{fontSize:10,color:"var(--c-muted)",marginTop:2}}>{lf.match_date||"—"}{lf.kickoff_time?` · ${lf.kickoff_time}`:""}{lf.venue?` · ${lf.venue}`:""}</div>
                </div>
                <div style={{textAlign:"right"}}>
                  {resolved.status==="confirmed"&&<span style={{fontWeight:800,fontSize:15}}>{resolved.home} – {resolved.away}</span>}
                  {resolved.status==="scheduled"&&<span style={{fontSize:11,color:"var(--c-muted2)",fontStyle:"italic"}}>Not played yet</span>}
                  {resolved.status==="disputed"&&<span style={{fontSize:11,fontWeight:700,color:"#ef4444"}}>⚠ Disputed</span>}
                </div>
              </div>
              {resolved.status==="disputed"&&(
                <div style={{marginTop:10,paddingTop:10,borderTop:`1px solid ${AS.borderColor}`}}>
                  <div style={{fontSize:11,color:"#ef4444",marginBottom:8}}>
                    {leagueTeamName(homeLt,detail.teamsById)} reported {resolved.homeReport.home}–{resolved.homeReport.away}, but {leagueTeamName(awayLt,detail.teamsById)} reported {resolved.awayReport.home}–{resolved.awayReport.away}. Both coaches should double check their match result.
                  </div>
                  {isOwner&&(overrideId===lf.id?(
                    <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                      <input type="number" value={overrideHome} onChange={e=>setOverrideHome(e.target.value)} placeholder="Home" style={{...inp,width:70}}/>
                      <span>–</span>
                      <input type="number" value={overrideAway} onChange={e=>setOverrideAway(e.target.value)} placeholder="Away" style={{...inp,width:70}}/>
                      <button onClick={()=>saveOverride(lf)} style={{padding:"7px 12px",borderRadius:6,border:"none",background:ac,color:"#fff",fontWeight:700,fontSize:11,cursor:"pointer"}}>Save Final Score</button>
                      <button onClick={()=>setOverrideId(null)} style={{padding:"7px 12px",borderRadius:6,border:`1px solid ${AS.borderColor}`,background:"none",color:"var(--c-muted)",fontSize:11,cursor:"pointer"}}>Cancel</button>
                    </div>
                  ):(
                    <button onClick={()=>setOverrideId(lf.id)} style={{padding:"7px 12px",borderRadius:6,border:"1px solid #ef4444",background:"none",color:"#ef4444",fontWeight:700,fontSize:11,cursor:"pointer"}}>Set Final Score (League Owner)</button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function PlayerLeaguesView({teamId,token,ac,AS}){
  const C={background:AS.surfaceColor,borderRadius:11,border:`1px solid ${AS.borderColor}`};
  const [leagues,setLeagues]=useState(null);
  const [activeLeagueId,setActiveLeagueId]=useState(null);
  const [activeTab,setActiveTab]=useState("table");
  const [detail,setDetail]=useState(null);

  useEffect(()=>{ loadLeagues(); },[teamId]);
  async function loadLeagues(){
    if(!teamId){setLeagues([]);return;}
    const lts=await api.get(token,"league_teams",`team_id=eq.${teamId}&status=eq.accepted&left_league_at=is.null&select=league_id`);
    const leagueIds=[...new Set((lts||[]).map(r=>r.league_id))];
    if(leagueIds.length===0){setLeagues([]);return;}
    const ls=await api.get(token,"leagues",`id=in.(${leagueIds.join(",")})`);
    setLeagues(ls||[]);
  }
  useEffect(()=>{ if(activeLeagueId)loadDetail(); },[activeLeagueId]);
  async function loadDetail(){
    const league=(leagues||[]).find(l=>l.id===activeLeagueId);
    const lts=await api.get(token,"league_teams",`league_id=eq.${activeLeagueId}`);
    const lfs=await api.get(token,"league_fixtures",`league_id=eq.${activeLeagueId}&order=match_date.asc`);
    const teamIds=[...new Set((lts||[]).filter(lt=>lt.team_id).map(lt=>lt.team_id))];
    let teamsById={};
    if(teamIds.length>0){const ts=await api.get(token,"teams",`id=in.(${teamIds.join(",")})&select=id,name`);(ts||[]).forEach(t=>{teamsById[t.id]=t;});}
    const fixtureIds=[...new Set((lfs||[]).flatMap(lf=>[lf.home_fixture_id,lf.away_fixture_id]).filter(Boolean))];
    let fixturesById={};
    if(fixtureIds.length>0){const fs=await api.get(token,"fixtures",`id=in.(${fixtureIds.join(",")})`);(fs||[]).forEach(f=>{fixturesById[f.id]=f;});}
    setDetail({league,leagueTeams:lts||[],leagueFixtures:lfs||[],fixturesById,teamsById});
  }

  if(leagues===null)return <div style={{padding:30,textAlign:"center",color:"var(--c-muted)"}}>Loading leagues…</div>;
  if(leagues.length===0)return <div style={{...C,padding:30,textAlign:"center",color:"var(--c-muted2)"}}>Not part of any leagues yet.</div>;

  if(!activeLeagueId){
    return(
      <div style={{display:"flex",flexDirection:"column",gap:10}}>
        {leagues.map(l=>(
          <button key={l.id} onClick={()=>setActiveLeagueId(l.id)} style={{...C,padding:"14px 16px",cursor:"pointer",textAlign:"left",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div style={{fontWeight:700,fontSize:14}}>{l.name}</div>
            <span style={{color:"var(--c-muted2)"}}>→</span>
          </button>
        ))}
      </div>
    );
  }
  if(!detail)return <div style={{padding:30,textAlign:"center",color:"var(--c-muted)"}}>Loading…</div>;
  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      <button onClick={()=>{setActiveLeagueId(null);setDetail(null);}} style={{alignSelf:"flex-start",background:"none",border:"none",color:ac,fontSize:11,fontWeight:700,cursor:"pointer",padding:0}}>← All Leagues</button>
      <div style={{fontSize:16,fontWeight:800}}>{detail.league.name}</div>
      <div style={{display:"flex",gap:3,background:AS.bgColor,borderRadius:8,padding:3,maxWidth:300}}>
        {[["table","📋 Table"],["fixtures","📅 Fixtures"],["stats","📊 Stats"]].map(([v,l])=>(
          <button key={v} onClick={()=>setActiveTab(v)} style={{flex:1,padding:"7px",borderRadius:6,border:"none",cursor:"pointer",fontSize:11,fontWeight:600,background:activeTab===v?ac:"transparent",color:activeTab===v?"#fff":"var(--c-muted)"}}>{l}</button>
        ))}
      </div>
      {activeTab==="table"&&<LeagueTableTab detail={detail} ac={ac} AS={AS} teamId={teamId}/>}
      {activeTab==="fixtures"&&(
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {detail.leagueFixtures.length===0?<div style={{...C,padding:24,textAlign:"center",color:"var(--c-muted2)",fontSize:12}}>No fixtures yet.</div>:detail.leagueFixtures.map(lf=>{
            const homeLt=detail.leagueTeams.find(lt=>lt.id===lf.home_league_team_id);
            const awayLt=detail.leagueTeams.find(lt=>lt.id===lf.away_league_team_id);
            const resolved=resolveLeagueFixtureScore(lf,detail.fixturesById);
            const isMyMatch=!!teamId&&(homeLt?.team_id===teamId||awayLt?.team_id===teamId);
            return(
              <div key={lf.id} style={{...C,padding:"11px 14px",display:"flex",justifyContent:"space-between",alignItems:"center",border:isMyMatch?`1px solid ${ac}`:C.border,background:isMyMatch?ac+"14":AS.surfaceColor}}>
                <div style={{display:"flex",alignItems:"center",gap:8,minWidth:0}}>
                  {isMyMatch&&<span title="Your team" style={{fontSize:11,flexShrink:0}}>⭐</span>}
                  <div style={{minWidth:0}}>
                    <div style={{fontWeight:600,fontSize:12}}>{leagueTeamName(homeLt,detail.teamsById)} vs {leagueTeamName(awayLt,detail.teamsById)}</div>
                    <div style={{fontSize:10,color:"var(--c-muted)"}}>{lf.match_date||"—"}</div>
                  </div>
                </div>
                {resolved.status==="confirmed"?<span style={{fontWeight:800}}>{resolved.home}–{resolved.away}</span>:<span style={{fontSize:10,color:resolved.status==="disputed"?"#ef4444":"var(--c-muted2)"}}>{resolved.status==="disputed"?"⚠ Disputed":"Upcoming"}</span>}
              </div>
            );
          })}
        </div>
      )}
      {activeTab==="stats"&&<LeagueStatsTab detail={detail} token={token} ac={ac} AS={AS}/>}
    </div>
  );
}

